import { Button, Checkbox, Input, message, Select, Spin, Tag } from "antd";
import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { serverFetch } from "../../server";
import Plugin from "./plugin";
import PluginView from "./plugin-view";
import "./plugins.css";

const Plugins = () => {
  const { pluginId: urlPluginId } = useParams();
  const navigate = useNavigate();
  const user = useSelector((s) => s.user);
  const [programs, setPrograms] = useState([]);
  const [selectedProgId, setSelectedProgId] = useState(null);
  const [plugins, setPlugins] = useState([]);
  const [selectedPlugin, setSelectedPlugin] = useState(null);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [loadingPlugins, setLoadingPlugins] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [tags, setTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    setLoadingPrograms(true);
    const progCompany = process.env.REACT_APP_PROG_COMPANY;
    const url = progCompany ? `dict/prog?progCompany=${progCompany}` : "dict/prog";
    serverFetch(url)
      .then((data) => {
        setLoadingPrograms(false);
        setPrograms(Array.isArray(data) ? data : []);
        if (data?.length > 0) {
          const saved = localStorage.getItem("plugins_selectedProgId");
          const exists = saved && data.some((p) => p.id === parseInt(saved, 10));
          setSelectedProgId(exists ? parseInt(saved, 10) : data[0].id);
        }
      })
      .catch((e) => {
        setLoadingPrograms(false);
        message.error(e?.userMessage || "Ошибка загрузки программ");
      });
  }, []);

  useEffect(() => {
    if (selectedProgId != null) localStorage.setItem("plugins_selectedProgId", String(selectedProgId));
  }, [selectedProgId]);

  useEffect(() => {
    if (!selectedProgId) {
      setTags([]);
      setSelectedTagIds([]);
      return;
    }
    serverFetch("Plugin/tags", { queryParams: { progId: selectedProgId } })
      .then((data) => setTags(Array.isArray(data) ? data : []))
      .catch(() => setTags([]));
    setSelectedTagIds([]);
  }, [selectedProgId]);

  const loadPlugins = () => {
    if (!selectedProgId) return;
    setLoadingPlugins(true);
    const queryParams = { progId: selectedProgId };
    if (user?.admin && showDeleted) queryParams.showDeleted = true;
    serverFetch("Plugin/plugins", { queryParams })
      .then((data) => {
        setLoadingPlugins(false);
        setPlugins(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        setLoadingPlugins(false);
        message.error(e?.userMessage || "Ошибка загрузки плагинов");
      });
  };

  useEffect(() => {
    if (!selectedProgId) {
      setPlugins([]);
      setSelectedPlugin(null);
      return;
    }
    if (!urlPluginId) setSelectedPlugin(null);
    loadPlugins();
  }, [selectedProgId, showDeleted]);

  const filteredByTags =
    selectedTagIds.length === 0
      ? plugins
      : plugins.filter((pl) => pl.tags?.some((t) => selectedTagIds.includes(t.id)));

  const searchLower = searchText.trim().toLowerCase();
  const filteredPlugins =
    searchLower === ""
      ? filteredByTags
      : filteredByTags.filter(
          (pl) =>
            (pl.name && pl.name.toLowerCase().includes(searchLower)) ||
            (pl.description && pl.description.toLowerCase().includes(searchLower))
        );

  useEffect(() => {
    if (!urlPluginId || filteredPlugins.length === 0) return;
    const id = parseInt(urlPluginId, 10);
    if (Number.isNaN(id)) return;
    const found = filteredPlugins.find((p) => p.id === id) ?? filteredByTags.find((p) => p.id === id) ?? plugins.find((p) => p.id === id);
    if (found) {
      setSelectedPlugin(found);
    } else {
      navigate("/plugins", { replace: true });
    }
  }, [urlPluginId, plugins]);

  const handleAddPlugin = () => {
    if (!selectedProgId) return;
    const draft = {
      progId: selectedProgId,
      name: "Новый плагин",
      act: true,
      description: "",
      needPayment: false,
      idx: plugins.length,
      ver: 1,
    };
    setSelectedPlugin(draft);
    navigate("/plugins");
  };

  const handlePluginSaved = () => {
    setSelectedPlugin(null);
    navigate("/plugins");
    loadPlugins();
  };

  const handlePluginDeleted = () => {
    setSelectedPlugin(null);
    navigate("/plugins");
    loadPlugins();
  };

  return (
    <div className="plugins-container">
      <div className="plugins-filters-row">
        <Select
          className="plugins-program-select"
          placeholder="Выберите программу"
          value={selectedProgId}
          onChange={setSelectedProgId}
          loading={loadingPrograms}
          options={programs.map((p) => ({ label: p.name, value: p.id }))}
        />
        {tags.length > 0 && (
          <Select
            mode="multiple"
            placeholder="Метки (фильтр)"
            value={selectedTagIds}
            onChange={setSelectedTagIds}
            options={tags.map((t) => ({ label: t.name, value: t.id }))}
            allowClear
            className="plugins-tags-select"
          />
        )}
        <Input
          placeholder="Поиск по названию и описанию"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          className="plugins-search-input"
        />
        {user?.admin && (
          <Checkbox
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
          >
            Показывать неактивные
          </Checkbox>
        )}
        {user?.admin && (
          <Button
            type="primary"
            onClick={handleAddPlugin}
            disabled={!selectedProgId}
            className="plugins-add-button"
          >
            Добавить
          </Button>
        )}
        <Button
          onClick={loadPlugins}
          disabled={!selectedProgId}
          loading={loadingPlugins}
        >
          Обновить
        </Button>
      </div>

      <div className="plugins-cards-wrap">
        {loadingPlugins ? (
          <div className="plugins-cards-loading">
            <Spin />
          </div>
        ) : filteredPlugins.length > 0 ? (
          <div className="plugins-cards">
            {filteredPlugins.map((pl) => (
              <div
                key={pl.id}
                className={`plugins-card ${selectedPlugin?.id === pl.id ? "selected" : ""}`}
                onClick={() => {
                  setSelectedPlugin(pl);
                  navigate(`/plugins/${pl.id}`);
                }}
              >
                <div className="plugins-card-body">
                  <div className={`plugins-card-name ${pl.act === false ? "plugins-card-name-inactive" : ""}`}>{pl.name || `#${pl.id}`}</div>
                  <div className="plugins-card-desc">{pl.description || ""}</div>
                  {pl.tags?.length > 0 && (
                    <div className="plugins-card-tags">
                      {pl.tags.map((t) => (
                        <Tag key={t.id} color={t.color || undefined}>
                          {t.name}
                        </Tag>
                      ))}
                    </div>
                  )}
                  {(pl.updatedOn || pl.needPayment === false || pl.qtyView != null || pl.qtyDownload != null) && (
                    <div className="plugins-card-footer">
                      {pl.updatedOn && (
                        <span className="plugins-card-updated">{new Date(pl.updatedOn).toLocaleString()}</span>
                      )}
                      {pl.needPayment === false && <Tag variant="outlined" color="gold">подписка</Tag>}
                      <span className="plugins-card-stats">
                        <span>{pl.qtyView ?? 0} <EyeOutlined /></span>
                        <span>{pl.qtyDownload ?? 0} <DownloadOutlined /></span>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="plugins-empty-state">
            <p>
              {plugins.length === 0
                ? "Нет плагинов по программе"
                : filteredByTags.length === 0
                  ? "Нет плагинов по выбранным меткам"
                  : "Нет плагинов по запросу"}
            </p>
          </div>
        )}
      </div>

      {selectedPlugin && (
        <>
          <div
            className="plugins-modal-overlay"
            onClick={() => {
              setSelectedPlugin(null);
              navigate("/plugins");
            }}
            aria-hidden
          />
          <div className="plugins-modal-panel">
            {user?.admin ? (
              <Plugin
                plugin={selectedPlugin}
                user={user}
                onSaved={handlePluginSaved}
                onCancel={() => {
                  setSelectedPlugin(null);
                  navigate("/plugins");
                }}
                onDeleted={handlePluginDeleted}
              />
            ) : (
              <PluginView
                plugin={selectedPlugin}
                user={user}
                onCancel={() => {
                  setSelectedPlugin(null);
                  navigate("/plugins");
                }}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Plugins;
